// src/js-dot/ui/style/functions/$properties.ts
import { toKebabCase } from "@js-dot/core";
function $properties(properties) {
  if (typeof properties === "string") {
    let propertyString = properties;
    const startsBrace = propertyString.includes("{");
    const endsBrace = propertyString.includes("}");
    if (!startsBrace) {
      if (endsBrace) {
        throw new ReferenceError("Property String should be starts with `{`");
      } else {
        propertyString = "{" + propertyString + "}";
      }
    } else if (!endsBrace) {
      throw new ReferenceError("Property String should be ends with `}`");
    }
    return propertyString;
  }
  const strings = [];
  for (let property in properties) {
    if (!properties.hasOwnProperty(property)) {
      continue;
    }
    strings[strings.length] = `${toKebabCase(property)}: ${properties[property]}`;
  }
  return `{
${strings.join(";\n")}
}`;
}

// src/js-dot/ui/style/functions/$rule.ts
function $rule(selector, properties) {
  return `${selector} ${$properties(properties)}`;
}

// src/js-dot/ui/style/functions/$keyframes.ts
function $keyframes(name, rules) {
  const strings = [];
  for (let time in rules) {
    if (!rules.hasOwnProperty(time)) {
      continue;
    }
    strings[strings.length] = $rule(time, rules[time]);
  }
  return `@keyframes ${name} {
${strings.join("")}
}`;
}

// src/js-dot/ui/style/functions/keyframes.ts
var Keyframes = class {
  constructor(rules) {
    this.rules = rules;
  }
  toRule(selector) {
    return $keyframes(selector, this.rules);
  }
};
function keyframes(rules) {
  return new Keyframes(rules);
}

// src/js-dot/ui/style/functions/$combine.ts
function $combine(target, source) {
  if (target && target["transform"] && source && source["transform"]) {
    target["transform"] += " " + source["transform"];
    delete source["transform"];
  }
  return {
    ...(target || {}),
    ...(source || {})
  };
}

// src/js-dot/ui/style/functions/$nth-animation-delay.ts
function $nthAnimationDelay(selector, interval, to = 12) {
  const properties = {};
  let from = 1;
  to++;
  while (++from < to) {
    properties[selector + `:nth-child(${from})`] = {
      animationDelay: (from - 1) * interval + "s"
    };
  }
  return properties;
}

// src/js-dot/ui/style/functions/get-var-name.ts
import { toKebabCase as toKebabCase2 } from "@js-dot/core";
function getVarName(name) {
  return "--" + toKebabCase2(name.startsWith("--") ? name.slice(2) : name);
}

// src/js-dot/ui/style/functions/get-var.ts
function getVar(element_or_name, names) {
  let element = element_or_name;
  if (typeof element_or_name === "string") {
    names = element_or_name;
    element = document.documentElement;
  }
  const computedStyle = getComputedStyle(element);
  if (Array.isArray(names)) {
    const map = {};
    let current = names.length;
    while (--current > 0) {
      const name = names[current];
      map[name] = computedStyle.getPropertyValue(getVarName(name)).trim();
    }
    return map;
  }
  if (typeof names === "object") {
    const map = {};
    for (let key in names) {
      const name = names[key];
      map[key] = computedStyle.getPropertyValue(getVarName(name)).trim();
    }
    return map;
  }
  return computedStyle.getPropertyValue(getVarName(names)).trim();
}

// src/js-dot/ui/style/functions/set-var.ts
function setVar(element_or_name, name_or_value, value) {
  let element = element_or_name;
  let name = name_or_value;
  if (typeof element_or_name === "string") {
    value = name_or_value;
    name = element_or_name;
    element = document.documentElement;
  }
  if (!name.startsWith("--")) {
    name = "--" + name;
  }
  element.style.setProperty(name, value);
}

// src/js-dot/ui/style/classes/styles.ts
var Styles = class {
  _rules = [];
  style;
  sheet;
  constructor(container) {
    const style = document.createElement("style");
    style.setAttribute("data-dot", this.constructor.name);
    (container || document.head).append(style);
    this.style = style;
    this.sheet = style.sheet;
  }
  add(rules_or_selector, index_or_properties, index) {
    let rules = rules_or_selector;
    if (typeof rules === "string" && typeof index_or_properties === "object") {
      rules = [{
        [rules]: index_or_properties
      }];
    } else if (!Array.isArray(rules)) {
      rules = [rules];
    }
    const {
      sheet,
      _rules
    } = this;
    if (index === void 0) {
      index = sheet.cssRules.length;
    }
    const end = rules.length;
    let current = -1;
    let currentIndex = index;
    while (++current < end) {
      const rule2 = rules[current];
      if (typeof rule2 === "string") {
        sheet.insertRule(rule2, currentIndex++);
        continue;
      }
      for (let selector in rule2) {
        const target = rule2[selector];
        sheet.insertRule(typeof target["toRule"] === "function" ? target.toRule(selector) : $rule(selector, target), currentIndex++);
      }
    }
    current = _rules.length;
    while (current-- > index) {
      _rules[current].from++;
    }
    const rule = new StyleRule(this, index);
    _rules[_rules.length] = rule;
    return rule;
  }
  remove(index) {
    const {
      _rules
    } = this;
    this.sheet.deleteRule(_rules.splice(index, 1)[0].from);
    let current = _rules.length;
    while (--current >= index) {
      _rules[current].from--;
    }
    return this;
  }
  reset() {
    const {
      sheet
    } = this;
    const {
      cssRules
    } = sheet;
    let current = cssRules.length;
    while (current-- > 0) {
      sheet.deleteRule(current);
    }
    this._rules = [];
    return this;
  }
  destroy() {
    const {
      style
    } = this;
    style.parentNode.removeChild(style);
    return this;
  }
};
var StyleRule = class {
  _sheet;
  from;
  to;
  constructor(sheet, from, to) {
    this._sheet = sheet;
    this.from = from;
    this.to = to || from + 1;
  }
  remove() {
    const {
      _sheet,
      from,
      to
    } = this;
    let current = from - 1;
    while (++current < to) {
      _sheet.remove(current);
    }
  }
};

// src/js-dot/ui/style/classes/style-factory.ts
var StyleFactory = class {
  _sheet;
  _store = {};
  _idFactory;
  _ruleFactory;
  constructor(factories_or_ruleFactory) {
    let factories = factories_or_ruleFactory;
    if (typeof factories === "function") {
      factories = {
        rule: factories
      };
    }
    this._sheet = new Styles();
    this._ruleFactory = factories.rule;
    this._idFactory = factories.id || defaultIdFactory;
  }
  update(...args) {
    const {
      _store
    } = this;
    const id = this._idFactory(...args);
    let rule = _store[id];
    if (rule) {
      return rule;
    }
    const ruleString = this._ruleFactory(...args);
    rule = this._sheet.add(ruleString);
    _store[id] = rule;
    return rule;
  }
};
function defaultIdFactory(...args) {
  return args.join("");
}
export { $combine, $keyframes, $nthAnimationDelay, $properties, $rule, Keyframes, StyleFactory, StyleRule, Styles, getVar, keyframes, setVar };
